import React, { useState, useRef, useEffect } from 'react';

import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Settings, ChevronDown, Building2, Check } from 'lucide-react';

const Header = ({ onToggleSidebar }) => {
  const { user, profile, signOut, userOrganizations, currentOrganization, switchOrganization } = useAuth();
  const { tenant, subdomain } = useTenant();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const menuRef = useRef(null);
  const orgDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setShowProfileMenu(false);
      }
      if (orgDropdownRef?.current && !orgDropdownRef?.current?.contains(event?.target)) {
        setShowOrgDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSwitchOrganization = async (tenantId) => {
    try {
      await switchOrganization(tenantId);
      setShowOrgDropdown(false);
      // Reload the page to refresh all data with new tenant context
      window.location?.reload();
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  };

  const getUserInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile?.first_name?.[0]}${profile?.last_name?.[0]}`?.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile?.first_name} ${profile?.last_name}`;
    }
    return user?.email || 'User';
  };

  // Get organization name to display (from tenant context or current organization)
  const getOrganizationName = () => {
    if (tenant?.name) {
      return tenant?.name;
    }
    if (currentOrganization?.tenantName) {
      return currentOrganization?.tenantName;
    }
    return 'Organization';
  };

  const hasMultipleOrganizations = userOrganizations?.length > 1;

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Left Section - Mobile Menu & Logo */}
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button - Always visible on mobile, hidden on desktop */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleSidebar}
            className="lg:hidden"
            aria-label="Toggle navigation menu"
          >
            <Icon name="Menu" size={20} />
          </Button>

          {/* Logo - Hidden on desktop where sidebar shows it, visible on mobile */}
          <div className="flex items-center space-x-3 lg:hidden">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Zap" size={20} color="white" />
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-semibold text-foreground">LocalProCRM</span>
              <span className="px-2 py-0.5 text-xs font-medium bg-accent text-accent-foreground rounded-full">
                v2.1
              </span>
            </div>
          </div>
        </div>

        {/* Right Side - Organization Switcher & User Profile */}
        <div className="flex items-center gap-4">
          {/* Organization Switcher - Only show if user has multiple organizations */}
          {hasMultipleOrganizations && (
            <div className="relative" ref={orgDropdownRef}>
              <button
                onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
              >
                <Building2 className="w-4 h-4 text-gray-600" />
                <div className="hidden md:flex flex-col items-start">
                  <span className="text-sm font-medium text-gray-900">
                    {getOrganizationName()}
                  </span>
                  <span className="text-xs text-gray-500">
                    {subdomain}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                  showOrgDropdown ? 'rotate-180' : ''
                }`} />
              </button>

              {/* Organization Dropdown */}
              {showOrgDropdown && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 max-h-96 overflow-y-auto">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Switch Organization
                    </p>
                  </div>
                  {userOrganizations?.map((org) => {
                    const isActive = org?.tenantId === (currentOrganization?.tenantId || profile?.tenant?.id);
                    return (
                      <button
                        key={org?.tenantId}
                        onClick={() => handleSwitchOrganization(org?.tenantId)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${
                          isActive ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex-shrink-0">
                          {org?.tenantLogoUrl ? (
                            <img
                              src={org?.tenantLogoUrl}
                              alt={org?.tenantName}
                              className="w-8 h-8 rounded object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
                              {org?.tenantName?.[0]?.toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <p className={`font-medium ${
                            isActive ? 'text-blue-900' : 'text-gray-900'
                          }`}>
                            {org?.tenantName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {org?.tenantSubdomain}
                            {org?.industryName && ` • ${org?.industryName}`}
                          </p>
                        </div>
                        {isActive && (
                          <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tenant Info - Only show if single organization */}
          {!hasMultipleOrganizations && profile?.tenant && (
            <div className="hidden md:flex flex-col items-end">
              <span className="text-sm font-medium text-gray-900">
                {profile?.tenant?.name}
              </span>
              <span className="text-xs text-gray-500">
                {profile?.tenant?.subdomain}.localprocrm.com
              </span>
            </div>
          )}

          {/* Profile Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-semibold">
                {getUserInitials()}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-900">
                  {getUserDisplayName()}
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {profile?.role?.replace('_', ' ') || 'User'}
                </p>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                showProfileMenu ? 'rotate-180' : ''
              }`} />
            </button>

            {/* Dropdown Menu */}
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                <div className="px-4 py-3 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-900">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.email}
                  </p>
                </div>

                <button
                  onClick={() => {
                    navigate('/settings');
                    setShowProfileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <User className="w-4 h-4" />
                  Profile Settings
                </button>

                <button
                  onClick={() => {
                    navigate('/settings');
                    setShowProfileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Settings className="w-4 h-4" />
                  Account Settings
                </button>

                <div className="border-t border-gray-200 my-1" />

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;